import React, { useState } from 'react';
import { Kid } from '../types';

interface QuestModalProps {
  isOpen: boolean;
  kid: Kid | undefined;
  onClose: () => void;
  onSubmit: (questData: {
    kid_id: string;
    name: string;
    icon_emoji: string;
    repeat_days: string[];
    time_str?: string;
    points: number;
  }) => Promise<void>;
}

const AVAILABLE_ICONS = [
  '⭐', '📚', '🎹', '🏊', '🏀', '⚽', '🎨', '📐',
  '🦷', '🛏️', '🥦', '🐶', '🧹', '✏️', '🎸', '💻',
];

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const QuestModal: React.FC<QuestModalProps> = ({
  isOpen,
  kid,
  onClose,
  onSubmit,
}) => {
  if (!isOpen || !kid) return null;

  const [name, setName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('⭐');
  const [selectedDays, setSelectedDays] = useState<string[]>(['Every Day']);
  const [timeStr, setTimeStr] = useState('');
  const [points, setPoints] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isEveryDay = selectedDays.includes('Every Day') || selectedDays.length === 7;

  const handleToggleDay = (day: string) => {
    if (day === 'Every Day') {
      setSelectedDays(['Every Day']);
      return;
    }

    let next: string[];
    if (selectedDays.includes('Every Day')) {
      next = [day];
    } else {
      if (selectedDays.includes(day)) {
        next = selectedDays.filter((d) => d !== day);
      } else {
        next = [...selectedDays, day];
      }
    }

    if (next.length === 0) {
      next = ['Every Day'];
    }
    setSelectedDays(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter a quest name');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await onSubmit({
        kid_id: kid.id,
        name: name.trim(),
        icon_emoji: selectedIcon,
        repeat_days: selectedDays,
        time_str: timeStr.trim() || undefined,
        points: Number(points) || 10,
      });
      // Reset form
      setName('');
      setSelectedIcon('⭐');
      setSelectedDays(['Every Day']);
      setTimeStr('');
      setPoints(10);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create quest');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-header">
          <h3 className="modal-title">
            New Quest for {kid.name} {kid.avatar_emoji}
          </h3>
          <button
            id="btn-close-quest-modal"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: '10px 14px',
              background: '#fee2e2',
              color: '#dc2626',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 700,
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="quest-name-input">
              Quest Name
            </label>
            <input
              id="quest-name-input"
              className="form-input"
              type="text"
              placeholder="e.g. Piano practice"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Pick an icon</label>
            <div className="icon-grid">
              {AVAILABLE_ICONS.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  className={`icon-option-btn ${selectedIcon === icon ? 'selected' : ''}`}
                  onClick={() => setSelectedIcon(icon)}
                  title={icon}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Repeat on</label>
            <div className="pills-row">
              <button
                type="button"
                className={`pill-btn ${isEveryDay ? 'selected' : ''}`}
                onClick={() => handleToggleDay('Every Day')}
              >
                Every Day
              </button>

              {DAYS_OF_WEEK.map((day) => {
                const isSelected = !isEveryDay && selectedDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    className={`pill-btn ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleToggleDay(day)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="form-row">
            <div>
              <label className="form-label" htmlFor="quest-time-input">
                Time (optional)
              </label>
              <input
                id="quest-time-input"
                className="form-input"
                type="time"
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
              />
            </div>

            <div>
              <label className="form-label" htmlFor="quest-points-input">
                ⭐ Points
              </label>
              <input
                id="quest-points-input"
                className="form-input"
                type="number"
                min="1"
                max="500"
                value={points}
                onChange={(e) => setPoints(Number(e.target.value))}
                required
              />
            </div>
          </div>

          <button
            id="btn-submit-add-quest"
            type="submit"
            className="btn-primary-gradient"
            disabled={submitting}
          >
            {submitting ? 'Adding...' : 'Add Quest! ✨'}
          </button>
        </form>
      </div>
    </div>
  );
};
